import { useState, useEffect, useRef } from "react";
import { flushSync } from "react-dom";
import { useLocation } from "wouter";
import { Header } from "@/components/Header";
import { ProgressIndicator } from "@/components/ProgressIndicator";
import { SmartAIChat } from "@/components/SmartAIChat";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { TopicNavigator } from "@/components/TopicNavigator";
import { useActiveTopic } from "@/hooks/use-active-topic";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { ArrowLeft, ArrowRight, RefreshCw, Undo2, Sparkles, ChevronUp, ChevronDown, Loader2 } from "lucide-react";
import { TopicSelector } from "@/components/TopicSelector";
import { useTest } from "@/context/TestContext";
import { QuestionCard, type SocratesQuestionData } from "@/components/QuestionCard";
import { type BlueprintDecision } from "@/components/RegenerateQuestionDialog";
import { useToast } from "@/hooks/use-toast";
import { ToastAction } from "@/components/ui/toast";
import { useRailAutoCondense } from "@/hooks/use-rail-auto-condense";
import { useLayoutState } from "@/hooks/use-layout-state";
import { readingLevelToGrade, type Blueprint, type Question } from "@shared/schema";
import { useChatNarrowWidth } from "@/hooks/use-chat-narrow-width";
import { useChatHalfHeight } from "@/hooks/use-chat-half-height";
import { sortQuestionAnswers, normalizeAnswerCapitalization } from "@/utils/sortAnswers";
import { buildQuestionIndexToTaskId } from "@/utils/socratesDuplicate";
import { apiClient } from "@/lib/apiClient";
import { withPreservedScroll } from "@/utils/preserveScroll";
import { AddItemControl, type AddItemLevel, type AddItemRun } from "@/components/AddItemControl";
import {
  generateSingleItem,
  AddItemTimeoutError,
  httpErrorFrom,
  regenerationToast,
} from "@/lib/generationRequests";
import { AddItemButton } from "@/components/AddItemButton";
import {
  loadPendingRegens,
  markQuestionPending,
  unmarkQuestionPending,
  markAnswerPending,
  unmarkAnswerPending,
} from "@/utils/regenPersistence";

// Hard cap on items per test (mirrors BuildBlueprint's MAX_TEST_ITEMS).
const MAX_TEST_ITEMS = 20;
const LEVEL_TO_ENUM: Record<AddItemLevel, Question["level"]> = {
  knowledge: "Knowledge",
  understanding: "Understanding",
  higherOrderThinking: "Higher-Order Thinking",
};
const LEVEL_TO_BLUEPRINT_FIELD: Record<Question["level"], "knowledge" | "understanding" | "higherOrderThinking"> = {
  "Knowledge": "knowledge",
  "Understanding": "understanding",
  "Higher-Order Thinking": "higherOrderThinking",
};

// Inverse of one confirmed blueprint amendment: Undo restores the old-level
// slot (fromField +1) and removes the new-level slot (toField -1) on the
// item's topic. Storing the per-operation delta rather than a full snapshot
// keeps Undo correct when two confirmed amendments run concurrently — a stale
// full snapshot would erase the other operation's valid move.
interface BlueprintInverse {
  topicId: string;
  fromField: "knowledge" | "understanding" | "higherOrderThinking";
  toField: "knowledge" | "understanding" | "higherOrderThinking";
}

// The regeneration a teacher confirmed, held until the response lands so the
// history entry (previous item + blueprint inverse) is pushed against the
// state that actually applied.
interface PendingRegenOp {
  previousVersion: Question;
  topicId: string;
  fromField: "knowledge" | "understanding" | "higherOrderThinking";
  toField: "knowledge" | "understanding" | "higherOrderThinking";
  blueprintDecision?: BlueprintDecision;
}

export default function ReviewTest() {
  const [, setLocation] = useLocation();
  const { blueprint, config, questions, setQuestions, setBlueprint } = useTest();
  // Freshest questions for handlers that run after an async wait (a card
  // Rescore resolving late): the closure-level `questions` is the snapshot at
  // the render that created the handler, not what a concurrent edit/regen
  // landed during the request.
  const questionsRef = useRef(questions);
  questionsRef.current = questions;
  const [assistantNarrow] = useChatNarrowWidth();
  const [assistantHalfHeight] = useChatHalfHeight();
  const { toast } = useToast();
  const { layoutState } = useLayoutState();
  
  // Track regeneration history for full undo functionality
  const [regenerationHistory, setRegenerationHistory] = useState<Array<{
    questionId: string;
    previousVersion: Question | Question[];
    action: 'question' | 'answer' | 'bulk-question' | 'edit' | 'delete';
    answerIndex?: number;
    // Original array position, so a deleted item can be re-inserted where it was.
    index?: number;
    // Per-operation inverse of a confirmed blueprint amendment, if one applied.
    blueprintInverse?: BlueprintInverse;
  }>>([]);

  // Result of one confirmed blueprint amendment attempt.
interface AmendmentOutcome {
  applied: boolean;
  reason?: "no-slot" | "standards" | "missing";
}

// Apply the confirmed move (old level -1, new level +1) as a single atomic
// operation over the ACTUAL prev supplied to setBlueprint. Eligibility is
// judged inside the updater against that prev, so the newest committed
// blueprint wins even when another surface edited it while the regeneration
// was in flight (e.g. the teacher revised it via Back to Blueprint). A page
// scoped ref mirror cannot survive such navigation. flushSync (already the
// in-repo idiom for observing a commit, see preserveScroll) makes the exact
// outcome observable synchronously, so the history inverse is recorded only
// when this updater consumed a real source slot. The updater itself is pure;
// the only side effect is an idempotent flag write.
const applyBlueprintAmendment = (
  setBlueprint: (updater: (prev: Blueprint | null) => Blueprint | null) => void,
  op: { topicId: string; fromField: "knowledge" | "understanding" | "higherOrderThinking"; toField: "knowledge" | "understanding" | "higherOrderThinking" },
): AmendmentOutcome => {
  let outcome: AmendmentOutcome = { applied: false, reason: "missing" };
  flushSync(() => {
    setBlueprint((prev) => {
      if (!prev) {
        outcome = { applied: false, reason: "missing" };
        return prev;
      }
      const topic = prev.topics.find((t) => t.id === op.topicId);
      if (!topic) {
        outcome = { applied: false, reason: "missing" };
        return prev;
      }
      const hasStandardsAllocation = (topic.standards ?? []).some(
        (s) => s.knowledge !== undefined || s.understanding !== undefined || s.higherOrderThinking !== undefined,
      );
      if (hasStandardsAllocation) {
        outcome = { applied: false, reason: "standards" };
        return prev;
      }
      if (topic[op.fromField] <= 0) {
        outcome = { applied: false, reason: "no-slot" };
        return prev;
      }
      outcome = { applied: true };
      return {
        ...prev,
        topics: prev.topics.map((t) =>
          t.id === op.topicId
            ? {
                ...t,
                [op.fromField]: t[op.fromField] - 1,
                [op.toField]: t[op.toField] + 1,
              }
            : t,
        ),
      };
    });
  });
  return outcome;
};

// Reverse one confirmed move (only ever recorded for an applied one): restore
// the old-level slot, remove the new-level slot.
const inverseBlueprintMove = (
  setBlueprint: (updater: (prev: Blueprint | null) => Blueprint | null) => void,
  inv: BlueprintInverse,
) => {
  setBlueprint((prev) =>
    prev
      ? {
          ...prev,
          topics: prev.topics.map((t) =>
            t.id === inv.topicId
              ? {
                  ...t,
                  [inv.fromField]: t[inv.fromField] + 1,
                  [inv.toField]: Math.max(0, t[inv.toField] - 1),
                }
              : t,
          ),
        }
      : prev
  );
};
  // Confirmed whole-item regens pending a response, so the history entry can
  // be pushed on success with the previous item and the exact move applied.
  const pendingRegenOpsRef = useRef(new Map<string, PendingRegenOp>());

  // Computed value for lockAll based on actual question states
  const lockAll = questions.every(q => q.locked);
  // Lock UI (lock-all toggles + locked counters) and per-topic "Regenerate
  // unlocked items" are hidden for now. All handlers/logic are kept — flip
  // these to true to bring the UI back.
  const SHOW_LOCK_UI = false;
  const SHOW_TOPIC_REGEN = false;
  const [focusedQuestion, setFocusedQuestion] = useState<{
    number: number;
    stem: string;
    answers: string[];
    correctAnswer: number;
    level: string;
  } | null>(null);
  
  // Mobile panel collapse state
  const [isMobilePanelCollapsed, setIsMobilePanelCollapsed] = useState(false);

  // Socrates Score data. The map is the in-render lookup; the canonical
  // copy lives on each question (`socratesScore` / `socratesScoreVoided`)
  // so refreshes don't fall back to the Blacksmith session's stale
  // pre-edit results.
  const [socratesMap, setSocratesMap] = useState<Map<string, SocratesQuestionData>>(new Map());
  const [socratesLoadingIds, setSocratesLoadingIds] = useState<Set<string>>(new Set());
  // Session-level BCE question_index → task_id map, built from the Socrates
  // score response. QuestionCard uses it to resolve a duplicate marker's
  // target index to the item currently on screen (see utils/socratesDuplicate).
  const [socratesIndexToTaskId, setSocratesIndexToTaskId] = useState<Map<number, string>>(new Map());

  // Track regeneration in flight so QuestionCard can show a spinner overlay
  // (whole-question) or per-row spinner (individual distractor).
  const [regeneratingQuestionIds, setRegeneratingQuestionIds] = useState<Set<string>>(new Set());
  const [regeneratingAnswers, setRegeneratingAnswers] = useState<Map<string, Set<number>>>(new Map());
  // Newly-added option slots being generated for the first time (distinct from
  // regeneration so the card can show "Generating Option…" rather than
  // "Regenerating answer…"). Transient — not persisted/replayed.
  const [addingAnswers, setAddingAnswers] = useState<Map<string, Set<number>>>(new Map());

  const markAnswerAdding = (id: string, answerIndex: number, on: boolean) => {
    setAddingAnswers((prev) => {
      const next = new Map(prev);
      const set = new Set(next.get(id) ?? []);
      if (on) set.add(answerIndex); else set.delete(answerIndex);
      if (set.size === 0) next.delete(id); else next.set(id, set);
      return next;
    });
  };

  const markQuestionRegenerating = (
    id: string,
    on: boolean,
    meta?: { targetLevel?: Question["level"]; blueprintDecision?: BlueprintDecision },
  ) => {
    setRegeneratingQuestionIds((prev) => {
      const next = new Set(prev);
      if (on) next.add(id); else next.delete(id);
      return next;
    });
    // Also persist so a refresh mid-regen can rehydrate the spinner and
    // re-fire the API call (see the mount effect below). The confirmed level
    // and blueprint decision ride along so a replay reproduces the operation
    // instead of silently rerunning at the old level.
    if (on) markQuestionPending(id, meta); else unmarkQuestionPending(id);
  };
  const markAnswerRegenerating = (id: string, answerIndex: number, on: boolean) => {
    setRegeneratingAnswers((prev) => {
      const next = new Map(prev);
      const set = new Set(next.get(id) ?? []);
      if (on) set.add(answerIndex); else set.delete(answerIndex);
      if (set.size === 0) next.delete(id); else next.set(id, set);
      return next;
    });
    if (on) markAnswerPending(id, answerIndex); else unmarkAnswerPending(id, answerIndex);
  };

  // Scroll-spy over the vertical topic sections — drives the left topic
  // navigator highlight and the compact selector on narrow screens.
  const topicIds = (blueprint?.topics ?? []).map((t) => t.id);
  const { activeId, scrollToTopic } = useActiveTopic(topicIds, 140);
  const [addingItem, setAddingItem] = useState(false);
  // The topic an in-flight AI generation is for — passed to AddItemControl so
  // the trigger keeps showing it even if that control unmounts/remounts (rail
  // collapse) mid-generation.
  const [addingTopicId, setAddingTopicId] = useState<string | null>(null);
  // Live phase + start time of an in-flight "Add item" generation, so the
  // control can show real progress instead of an opaque spinner (ARC-30 cap 3).
  const [addItemRun, setAddItemRun] = useState<AddItemRun | null>(null);
  // Freshly-added items get a blue border + "New" badge until seen (see
  // QuestionCard's in-view timer, which calls handleItemSeen).
  const [newItemIds, setNewItemIds] = useState<Set<string>>(new Set());
  // Just-regenerated items get the same highlight, with a "Regenerated" badge.
  const [regeneratedItemIds, setRegeneratedItemIds] = useState<Set<string>>(new Set());
  // Per-option regen highlight: question id -> set of regenerated answer indices.
  const [regeneratedOptionIds, setRegeneratedOptionIds] = useState<Map<string, Set<number>>>(new Map());
  // Resolve a question's card-level highlight for QuestionCard (new precedence).
  const highlightFor = (id: string): "new" | "regenerated" | undefined =>
    newItemIds.has(id) ? "new" : regeneratedItemIds.has(id) ? "regenerated" : undefined;
  // toast key -> dismiss() fns. Card/add toasts are keyed by item id; option
  // toasts by `${id}::${index}` so we can dismiss the toast for a specific item
  // OR a specific option. Toasts stack (TOAST_LIMIT) rather than replacing.
  const itemToastDismiss = useRef<Map<string, Array<() => void>>>(new Map());
  const dismissToastKey = (key: string) => {
    itemToastDismiss.current.get(key)?.forEach((d) => d());
    itemToastDismiss.current.delete(key);
  };
  // Dismiss every toast tied to an item: its card toast + all its option toasts.
  const dismissItemToasts = (id: string) => {
    for (const key of Array.from(itemToastDismiss.current.keys())) {
      if (key === id || key.startsWith(`${id}::`)) dismissToastKey(key);
    }
  };
  // Seeing an item (in view ~5s, or clicking its "New"/"Regenerated" badge)
  // clears ALL of its highlights and toasts.
  const handleItemSeen = (id: string) => {
    const dropSet = (prev: Set<string>) => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      return next;
    };
    setNewItemIds(dropSet);
    setRegeneratedItemIds(dropSet);
    setRegeneratedOptionIds((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Map(prev);
      next.delete(id);
      return next;
    });
    dismissItemToasts(id);
  };
  // Clicking one option's "Regenerated" badge clears just that option's
  // highlight and its toast.
  const handleOptionSeen = (id: string, index: number) => {
    setRegeneratedOptionIds((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Map(prev);
      const set = new Set(next.get(id) ?? []);
      set.delete(index);
      if (set.size === 0) next.delete(id);
      else next.set(id, set);
      return next;
    });
    dismissToastKey(`${id}::${index}`);
  };
  const scrollToItem = (id: string) =>
    document
      .getElementById(`question-card-${id}`)
      ?.scrollIntoView({ behavior: "smooth", block: "center" });
  // Persistent highlight toast with a button that jumps to the item. toastKey
  // lets us later dismiss this exact toast (per item or per option).
  const notifyHighlight = (toastKey: string, scrollId: string, title: string, description: string) => {
    const { dismiss } = toast({
      variant: "info",
      title,
      description,
      duration: Infinity,
      action: (
        <ToastAction altText="Scroll to the item" onClick={() => scrollToItem(scrollId)}>
          View item
        </ToastAction>
      ),
    });
    const list = itemToastDismiss.current.get(toastKey) ?? [];
    list.push(dismiss);
    itemToastDismiss.current.set(toastKey, list);
  };
  // Mark a whole item as just-regenerated: card highlight + persistent toast.
  const markRegenerated = (id: string, title: string, description: string) => {
    setRegeneratedItemIds((prev) => new Set(prev).add(id));
    notifyHighlight(id, id, title, description);
  };
  // Mark a single option as just-regenerated: option-level highlight + toast.
  const markRegeneratedOption = (id: string, index: number, title: string, description: string) => {
    setRegeneratedOptionIds((prev) => {
      const next = new Map(prev);
      const set = new Set(next.get(id) ?? []);
      set.add(index);
      next.set(id, set);
      return next;
    });
    notifyHighlight(`${id}::${index}`, id, title, description);
  };
  // lg-only: collapse the summary + add-item block to give the assistant more
  // vertical room. Condensed by default; Undo/Finalize stay visible regardless.
  const [condenseOptions, setCondenseOptions] = useState(true);
  // xl left rail: same condensable card, but expanded by default — the card
  // bounds its own height (summary scrolls internally, Undo stays pinned), so
  // there's no reason to start collapsed when there's room.
  const [railCondensed, setRailCondensed] = useState(false);
  const railRef = useRef<HTMLDivElement>(null);
  const userSetRail = useRef(false);
  // Auto-condense the rail card when expanded it would cover the topics; a
  // manual open is respected until the next resize.
  useRailAutoCondense(railRef, railCondensed, setRailCondensed, userSetRail, questions.length);

  // Guard: if we arrive at ReviewTest with no questions/blueprint, redirect
  // once on mount. Using a useEffect (not render-body setLocation) to avoid
  // triggering a state update during render, which caused GenerateTest to
  // remount with a fresh hasStartedGeneration ref and fire a second generation.
  useEffect(() => {
    if (!blueprint || questions.length === 0) {
      setLocation("/create/generate");
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // DIAG-REVIEW-MUTATIONS: log every change to the questions array so we can
  // tell whether items are being replaced by a user-triggered handler or a
  // hidden auto-trigger. Stack trace shows the caller. Remove after RCA.
  useEffect(() => {
    // eslint-disable-next-line no-console
    console.log(
      "[DIAG] questions array changed at",
      new Date().toISOString(),
      "count=" + questions.length,
      "ids=" + questions.map(q => q.id).join(","),
      "stems=" + JSON.stringify(questions.map(q => (q.stem || "").slice(0, 40))),
      "\nstack:\n" + new Error().stack,
    );
  }, [questions]);

  // Extract sessionId once — it doesn't change after generation
  const sessionId = questions.find(q => q.blacksmithSessionId)?.blacksmithSessionId;

  useEffect(() => {
    // First, hydrate the map from each question's persisted Socrates score
    // (set by `scoreAddedItem` / the full-regen polling IIFE / manual
    // re-score, then written into localStorage by TestContext). This is
    // canonical — it matches the question's current stem + options.
    const hydrated = new Map<string, SocratesQuestionData>();
    for (const q of questions) {
      if (q.socratesScore) hydrated.set(q.id, q.socratesScore as SocratesQuestionData);
    }
    if (hydrated.size > 0) setSocratesMap(hydrated);

    if (!sessionId) return;

    // Fall back to the session-cached results for questions that have
    // neither a stored score nor a voided marker. Voided means "we cleared
    // this on edit and the auto-score hasn't landed (or failed)" — the
    // session's score for that slot is for the pre-edit options, so we
    // must NOT overwrite the empty cell with it.
    fetch(`/api/socrates-score/${sessionId}`)
      .then(res => res.json())
      .then(data => {
        if (!data.available || !data.questions) return;
        // Non-positional identity layer for the duplicate advisory: the map
        // survives reordering and edits, so a marker's session index can
        // still be resolved to the item the teacher currently sees.
        setSocratesIndexToTaskId(buildQuestionIndexToTaskId(data.questions));
        // Read the latest questions via the setQuestions updater so any
        // edits that landed during the fetch (e.g. a voided slot) are
        // respected. The updater itself stays pure; the in-memory map sync
        // runs after.
        let additions: Array<[string, SocratesQuestionData]> = [];
        setQuestions(prevQuestions => {
          additions = [];
          const matchedTaskIds = new Set<string>();
          const updated = prevQuestions.map((q, qi) => {
            // Prefer stable task_id join so feedback survives reordering
            // (sort by cognitive level, manual drag, etc.). Fall back to
            // position-based question_index for historical records that
            // predate the task_id field.
            const socratesQ = data.questions.find((sq: SocratesQuestionData) => {
              if (sq.task_id && q.taskId) {
                if (sq.task_id === q.taskId) { matchedTaskIds.add(sq.task_id); return true; }
                return false;
              }
              return sq.question_index === qi;
            });
            if (!socratesQ) return q;
            if (q.socratesScoreVoided) return q;
            if (q.socratesScore) return q;
            additions.push([q.id, socratesQ]);
            return { ...q, socratesScore: socratesQ };
          });
          // Dev-time warning: feedback records whose task_id matched nothing
          // in the displayed list — possible session/questions desync.
          if (process.env.NODE_ENV !== 'production') {
            for (const sq of data.questions as SocratesQuestionData[]) {
              if (sq.task_id && !matchedTaskIds.has(sq.task_id)) {
                console.warn(
                  `[Socrates] feedback task_id "${sq.task_id}" (question_index=${sq.question_index}) matched no displayed question — possible desync`,
                );
              }
            }
          }
          return additions.length > 0 ? updated : prevQuestions;
        });
        if (additions.length > 0) {
          setSocratesMap(prev => {
            const next = new Map(prev);
            for (const [id, score] of additions) next.set(id, score);
            return next;
          });
        }
      })
      .catch((err) => {
        console.warn("[Socrates] Failed to load socrates data:", err);
      });
  }, [sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Render guard — prevents rendering without data while the mount effect
  // above arranges the redirect. No setLocation here (that was the race).
  if (!blueprint || questions.length === 0) {
    return null;
  }

  const questionsByTopic = blueprint.topics.map((topic) => ({
    topic,
    questions: questions.filter((q) => q.topicId === topic.id),
  }));

  const handleUpdateQuestion = (updatedQuestion: Question) => {
    // Manual edits push their own undo entry so they're independently
    // reversible from regenerations. Lock toggles and other non-content
    // changes don't push (stem / answers / correctAnswer must differ).
    const previous = questions.find((q) => q.id === updatedQuestion.id);
    let contentChanged = false;
    if (previous) {
      contentChanged =
        previous.stem !== updatedQuestion.stem ||
        previous.correctAnswer !== updatedQuestion.correctAnswer ||
        previous.answers.length !== updatedQuestion.answers.length ||
        previous.answers.some((a, i) => a !== updatedQuestion.answers[i]);
      if (contentChanged) {
        setRegenerationHistory((prev) => [
          ...prev,
          { questionId: updatedQuestion.id, previousVersion: { ...previous }, action: 'edit' },
        ]);
      }
    }
    // On a content change, void any prior Socrates score — the badge
    // referred to different content. Modal-driven saves call
    // handleSocratesUpdate right after with a fresh score, which clears
    // the void.
    const next = contentChanged
      ? { ...updatedQuestion, socratesScore: undefined, socratesScoreVoided: true }
      : updatedQuestion;
    setQuestions(
      questions.map((q) =>
        q.id === updatedQuestion.id ? next : q
      )
    );
    if (contentChanged) {
      setSocratesMap(prev => {
        const m = new Map(prev);
        m.delete(updatedQuestion.id);
        return m;
      });
    }
  };

  const handleRegenerateQuestion = async (
    questionId: string,
    targetLevel?: Question["level"],
    blueprintDecision?: BlueprintDecision,
  ) => {
    const question = questions.find((q) => q.id === questionId);
    if (!question) return;
    // Capture the global position now — by the time the toast fires,
    // setQuestions has replaced the array reference, so a fresh
    // indexOf(question) wouldn't match.
    const questionNumber = questions.findIndex((q) => q.id === questionId) + 1;
    // Whole-item regen targets the level chosen in the dialog. A legacy replay
    // from the mount-time pending list may carry no choice; it keeps the
    // current level, preserving the pre-dialog behavior on refresh.
    const target = targetLevel ?? question.level;
    const topic = blueprint?.topics.find(t => t.id === question.topicId);
    const fromField = LEVEL_TO_BLUEPRINT_FIELD[question.level];
    const toField = LEVEL_TO_BLUEPRINT_FIELD[target];

    // What the teacher confirmed, held until the response lands so the
    // history entry reflects the move that actually applied. Concurrent
    // confirmed regens each register their own op here.
    pendingRegenOpsRef.current.set(questionId, {
      previousVersion: { ...question },
      topicId: question.topicId,
      fromField,
      toField,
      blueprintDecision,
    });

    // Drop the stale Socrates result immediately so no wrong score shows
    // during the up-to-12 s polling window. The existing poll repopulates it.
    clearSocratesFor(questionId);
    markQuestionRegenerating(questionId, true, { targetLevel: target, blueprintDecision });
    try {
      // apiClient injects the Bearer token from localStorage — the
      // regenerate endpoint requires it (authMiddleware on the server).
      const response = await apiClient("/api/questions/regenerate", {
        method: "POST",
        body: JSON.stringify({
          question: {
            stem: question.stem,
            answers: question.answers,
            correctAnswer: question.correctAnswer,
            level: target,
            difficulty: question.difficulty,
          },
          topicName: topic?.name || "General",
          grade: readingLevelToGrade(config?.readingLevel),
          standards: question.standards,
          // Whole-item regen takes a fresh-item target so Blacksmith writes a
          // new MCQ at the requested level instead of the distractor prompt.
          regenerationTarget: "question",
          // Every other existing item on the test, for the server's cross-item
          // duplication check. Excludes the item being regenerated.
          existingItems: questions
            .filter((q) => q.id !== questionId)
            .map((q) => ({ stem: q.stem, answers: q.answers })),
        }),
      });

      if (!response.ok) throw await httpErrorFrom(response);
      const data = await response.json();

      // Merge against the LATEST questions array — a concurrent regeneration
      // elsewhere must not be stamped over by a stale captured snapshot.
      setQuestions((prev) =>
        prev.map((q) =>
          q.id === questionId
            ? sortQuestionAnswers(normalizeAnswerCapitalization({
                ...q,
                stem: data.stem,
                answers: data.answers,
                correctAnswer: data.correctAnswer,
                level: target,
                duplicate_warning: data.duplicate_warning,
                // Whole-item regen — the polling IIFE below fills in the
                // new score; mark the slot voided meanwhile so a refresh
                // doesn't keep showing the pre-regen score.
                socratesScore: undefined,
                socratesScoreVoided: true,
              }))
            : q
        )
      );
      markRegenerated(questionId, "Question regenerated", `This item has been regenerated.`);

      // One toast regardless of how many reasons the flag carries.
      if (data.duplicate_warning?.flagged) {
        toast({
          title: "Possible duplicate",
          description: "This item may be similar to an existing item.",
        });
      }

      // Teacher confirmed the new level becomes intent: move the requested
      // count on this item's topic from the old level to the new one. The
      // topic total and percentage stay untouched. The eligibility and the
      // mutation are ONE operation over the actual prev supplied to
      // setBlueprint, so the newest committed blueprint wins even if the
      // teacher revised it on another page while this response was in flight.
      // Neither a missing slot nor a standards allocation may manufacture or
      // drift approved counts; those route to Back to Blueprint.
      const op = pendingRegenOpsRef.current.get(questionId);
      pendingRegenOpsRef.current.delete(questionId);
      let amendmentApplied = false;
      if (op && op.blueprintDecision === 'update') {
        const outcome = applyBlueprintAmendment(setBlueprint, {
          topicId: op.topicId,
          fromField: op.fromField,
          toField: op.toField,
        });
        amendmentApplied = outcome.applied;
        if (!outcome.applied && outcome.reason) {
          const reason = outcome.reason === "standards"
            ? "this topic's standards define their own level counts"
            : "the blueprint has no slot at the item's current level";
          toast({
            title: "Blueprint not changed",
            description: `${reason}. Use Back to Blueprint to adjust the requested levels.`,
            duration: Infinity,
            action: (
              <ToastAction altText="Back to Blueprint" onClick={() => setLocation("/create/review-blueprint")}>
                Back to Blueprint
              </ToastAction>
            ),
          });
        }
      }

      // History is pushed on success so entries land in completion order and
      // Undo walks them in reverse-completion order; the per-operation inverse
      // makes that undo safe under concurrency.
      if (op) {
        setRegenerationHistory(prev => [...prev, {
          questionId: questionId,
          previousVersion: op.previousVersion,
          action: 'question',
          ...(amendmentApplied
            ? { blueprintInverse: { topicId: op.topicId, fromField: op.fromField, toField: op.toField } }
            : {}),
        }]);
      }
      if (data.blacksmithSessionId) {
        setSocratesLoadingIds(prev => new Set(Array.from(prev).concat(questionId)));
        (async () => {
          let attempts = 0;
          while (attempts < 4) {
            if (attempts > 0) await new Promise(r => setTimeout(r, 3000));
            try {
              const resp = await fetch(`/api/socrates-score/${data.blacksmithSessionId}`);
              const socratesResult = await resp.json();
              if (socratesResult.available && socratesResult.questions?.length > 0) {
                const score = socratesResult.questions[0];
                setSocratesMap(prev => {
                  const next = new Map(prev);
                  next.set(questionId, score);
                  return next;
                });
                // Persist alongside the question so a refresh keeps it.
                setQuestions(prev => prev.map(q =>
                  q.id === questionId ? { ...q, socratesScore: score, socratesScoreVoided: false } : q,
                ));
                break;
              }
            } catch { /* swallow, retry */ }
            attempts++;
          }
          setSocratesLoadingIds(prev => {
            const next = new Set(prev);
            next.delete(questionId);
            return next;
          });
        })();
      }
    } catch (error) {
      console.error("Failed to regenerate question:", error);
      toast({
        ...regenerationToast(error, "Could not regenerate the question. Please try again."),
      });
      // No history entry was pushed for this op, so nothing to roll back.
      pendingRegenOpsRef.current.delete(questionId);
    } finally {
      markQuestionRegenerating(questionId, false);
    }
  };

  const handleRegenerateAnswer = async (questionId: string, answerIndex: number) => {
    const question = questions.find((q) => q.id === questionId);
    if (!question) return;
    const questionNumber = questions.findIndex((q) => q.id === questionId) + 1;

    setRegenerationHistory(prev => [...prev, {
      questionId: questionId,
      previousVersion: { ...question },
      action: 'answer',
      answerIndex: answerIndex
    }]);

    const topic = blueprint?.topics.find(t => t.id === question.topicId);

    const wasCorrectAnswer = answerIndex === question.correctAnswer;

    markAnswerRegenerating(questionId, answerIndex, true);
    try {
      const response = await apiClient("/api/questions/regenerate", {
        method: "POST",
        body: JSON.stringify({
          question: {
            stem: question.stem,
            answers: question.answers,
            correctAnswer: question.correctAnswer,
            level: question.level,
            difficulty: question.difficulty,
          },
          topicName: topic?.name || "General",
          grade: readingLevelToGrade(config?.readingLevel),
          standards: question.standards,
          // Tells the server we want a different correct answer (not just a
          // new distractor). Blacksmith adapts its prompt accordingly.
          regenerationTarget: wasCorrectAnswer ? 'correct' : 'distractor',
        }),
      });

      if (!response.ok) throw await httpErrorFrom(response);
      const data = await response.json();

      // For correct-answer regen, splice in the NEW question's correct
      // answer text (which Blacksmith was instructed to make different
      // from the old one). For distractor regen, keep splicing in the
      // answer at the same index — the new question's option at that
      // slot is what we want.
      // For a distractor regen, splice in a NEW distractor — never the
      // returned correct answer (the server now keeps the same correct
      // answer and only refreshes distractors), and prefer one not already
      // present in this question.
      const newAnswerText = wasCorrectAnswer
        ? data.answers[data.correctAnswer]
        : (data.answers.find((a: string, i: number) => i !== data.correctAnswer && !question.answers.includes(a)) ??
           data.answers.find((_: string, i: number) => i !== data.correctAnswer) ??
           data.answers[answerIndex]);
      const updatedAnswers = [...question.answers];
      updatedAnswers[answerIndex] = newAnswerText;
      const updatedQuestion = sortQuestionAnswers(
        normalizeAnswerCapitalization({ ...question, answers: updatedAnswers }),
      );
      setQuestions(questions.map((q) => (q.id === questionId ? updatedQuestion : q)));
      // Options changed — the old Socrates result is now against different
      // option text and must not be shown.
      clearSocratesFor(questionId);

      markRegeneratedOption(
        questionId,
        answerIndex,
        "Option regenerated",
        `Option ${String.fromCharCode(65 + answerIndex)} has been regenerated.`,
      );
      // Options changed — the old Socrates result is now against different
      // option text and must not be shown.
      clearSocratesFor(questionId);

      // Auto-re-score against the new option text so the card doesn't sit
      // unscored after a regen (matches the auto-score on item add).
      void scoreAddedItem(questionId, updatedQuestion);
    } catch (error) {
      console.error("Failed to regenerate answer:", error);
      toast({
        ...regenerationToast(error, "Could not regenerate the answer. Please try again."),
      });
      setRegenerationHistory(prev => prev.slice(0, -1));
    } finally {
      markAnswerRegenerating(questionId, answerIndex, false);
    }
  };

  const handleDeleteQuestion = (questionId: string) => {
    const index = questions.findIndex((q) => q.id === questionId);
    if (index === -1) return;
    const removed = questions[index];
    setQuestions(questions.filter((q) => q.id !== questionId));
    // Record on the undo stack so the deletion can be reversed.
    setRegenerationHistory((prev) => [
      ...prev,
      { questionId, previousVersion: removed, action: 'delete', index },
    ]);
    toast({ title: "Item deleted", description: "Removed from the test — use Undo to restore it." });
  };

  // Insert a question right after the last existing item of its topic, so it
  // joins that topic's section and the running item numbers stay sensible.
  const insertQuestionInTopic = (newQ: Question) =>
    setQuestions((prev) => {
      const idxs = prev.map((q, i) => (q.topicId === newQ.topicId ? i : -1)).filter((i) => i >= 0);
      const at = idxs.length ? idxs[idxs.length - 1] + 1 : prev.length;
      const copy = [...prev];
      copy.splice(at, 0, newQ);
      return copy;
    });

  // Run Socrates validation against the given question shape — used both
  // when a fresh item is added AND after an option-level regen replaces
  // text on an existing item. Fire-and-forget: the card shows "Scoring…"
  // meanwhile. Server accepts 2–6 options; anything outside that is a
  // blank/incomplete card and isn't worth scoring yet.
  const scoreAddedItem = async (id: string, q: Question) => {
    if (!q.answers || q.answers.length < 2 || q.answers.length > 6) return;
    if (!q.stem?.trim()) return;
    // Each state change can grow/shrink the (possibly off-screen) new card, so
    // preserve scroll around them too — adding an item should never move the view.
    withPreservedScroll(() => setSocratesLoadingIds((prev) => new Set(prev).add(id)));
    try {
      const resp = await apiClient("/api/questions/score", {
        method: "POST",
        body: JSON.stringify({
          stem: q.stem,
          answers: q.answers,
          correctAnswer: q.correctAnswer,
          targetGrade: readingLevelToGrade(config?.readingLevel),
        }),
      });
      if (resp.ok) {
        const score = await resp.json();
        withPreservedScroll(() => {
          setSocratesMap((prev) => new Map(prev).set(id, score));
          // Persist the score onto the question so a refresh shows it
          // (instead of falling back to the session's stale pre-edit data).
          // The isolated endpoint scores q.answers verbatim, so this score's
          // letters already match the displayed order. Drop any earlier
          // sort's answerLetterMap: keeping it would remap these fresh
          // letters through a map for a different order.
          setQuestions((prev) =>
            prev.map((q) =>
              q.id === id
                ? { ...q, socratesScore: score, socratesScoreVoided: false, answerLetterMap: undefined }
                : q,
            ),
          );
        });
      }
    } catch {
      /* leave unscored — the user can still score it from Edit */
    } finally {
      withPreservedScroll(() =>
        setSocratesLoadingIds((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        }),
      );
    }
  };

  const handleAddItem = async ({ topicId, level, mode }: { topicId: string; level: AddItemLevel; mode: "ai" | "blank" }) => {
    if (questions.length >= MAX_TEST_ITEMS) {
      toast({ title: "Item limit reached", description: `Tests are capped at ${MAX_TEST_ITEMS} items.`, variant: "destructive" });
      return;
    }
    const topic = blueprint?.topics.find((t) => t.id === topicId);
    if (!topic) return;
    const newId = `q-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

    if (mode === "blank") {
      // Insert without sliding the user's view around (no auto-scroll either).
      withPreservedScroll(() => {
        insertQuestionInTopic({
          id: newId, topicId, level: LEVEL_TO_ENUM[level],
          stem: "", answers: ["", "", "", ""], correctAnswer: 0,
          locked: false, stemLocked: false, answersLocked: false,
        });
        setNewItemIds((prev) => new Set(prev).add(newId));
      });
      notifyHighlight(newId, newId, "Blank item added", `Added to ${topic.name}. Use Edit to fill it in.`);
      return;
    }

    setAddingItem(true);
    setAddingTopicId(topicId);
    // ARC-30 cap 3: this used to be one POST that held the connection open for
    // up to 8 minutes with nothing to show for it. generateSingleItem() submits,
    // polls the real BCE session and retrieves — same start/status/finish shape
    // batch generation uses — and reports each phase so the control can show
    // what is happening and for how long.
    setAddItemRun({ startedAt: Date.now(), phase: "submitting", degraded: false });
    try {
      if (!config) throw new Error("no test config available");
      const { question, duplicate_warning } = await generateSingleItem(
        {
          topic: { id: topic.id, name: topic.name, standards: topic.standards },
          level,
          config,
          // The generated item is not among these yet — every current item on
          // the test goes to the server's cross-item duplication check.
          existingItems: questions.map((q) => ({ stem: q.stem, answers: q.answers })),
        },
        {
          token: localStorage.getItem("auth_token"),
          onProgress: ({ phase, degraded }) =>
            setAddItemRun((prev) => (prev ? { ...prev, phase, degraded } : prev)),
        },
      );
      const added = { ...question, id: newId, topicId, duplicate_warning } as Question;
      // Insert without sliding the user's view around — a generated item lands
      // asynchronously and the blue highlight + persistent toast's "View item"
      // button let them go to it when they choose.
      withPreservedScroll(() => {
        insertQuestionInTopic(added);
        setNewItemIds((prev) => new Set(prev).add(newId));
      });
      notifyHighlight(newId, newId, "Item generated", `Added to ${topic.name}.`);
      // One toast regardless of how many reasons the flag carries.
      if (duplicate_warning?.flagged) {
        toast({ title: "Possible duplicate", description: "This item may be similar to an existing item." });
      }
      void scoreAddedItem(newId, added);
    } catch (err) {
      console.error("Add item failed:", err);
      // "We stopped waiting" is not "it failed" — the item is still being
      // generated server-side, and telling the user it failed is what makes
      // them start over and pay for it twice (ARC-30).
      const stillRunning = err instanceof AddItemTimeoutError;
      toast({
        title: stillRunning ? "Still generating" : "Couldn't generate item",
        description: stillRunning
          ? "This item is taking longer than usual and is still being written. Try adding it again in a minute, or add a blank item meanwhile."
          : "Blacksmith may be busy — try again, or add a blank item.",
        variant: stillRunning ? undefined : "destructive",
      });
    } finally {
      setAddingItem(false);
      setAddingTopicId(null);
      setAddItemRun(null);
    }
  };

  // Append a new answer option and fill it via the same regenerate-option
  // path. Blacksmith still returns ~4 options at base; we splice a fresh one
  // into the new slot.
  const handleAddOption = async (questionId: string) => {
    const question = questions.find((q) => q.id === questionId);
    if (!question || question.answers.length >= 6) return;

    const newIndex = question.answers.length;
    const questionNumber = questions.findIndex((q) => q.id === questionId) + 1;
    // Commit a placeholder so the new row shows immediately with a spinner.
    setQuestions(questions.map((q) => (q.id === questionId ? { ...q, answers: [...q.answers, ""] } : q)));
    // Adding an option changes the question — drop the now-stale Socrates score.
    clearSocratesFor(questionId);
    markAnswerAdding(questionId, newIndex, true);

    const topic = blueprint?.topics.find((t) => t.id === question.topicId);
    try {
      const response = await apiClient("/api/questions/regenerate", {
        method: "POST",
        body: JSON.stringify({
          question: {
            stem: question.stem,
            answers: question.answers,
            correctAnswer: question.correctAnswer,
            level: question.level,
            difficulty: question.difficulty,
          },
          topicName: topic?.name || "General",
          grade: readingLevelToGrade(config?.readingLevel),
          standards: question.standards,
          regenerationTarget: 'distractor',
          // Other items on the test for the server's cross-item duplicate
          // check. Excludes the item getting the new option.
          existingItems: questions
            .filter((q) => q.id !== questionId)
            .map((q) => ({ stem: q.stem, answers: q.answers })),
        }),
      });
      if (!response.ok) throw await httpErrorFrom(response);
      const data = await response.json();
      // A fresh distractor for the new slot: not the returned correct answer,
      // and not already one of this question's options.
      const candidate: string =
        (Array.isArray(data.answers)
          ? data.answers.find((a: string, i: number) => i !== data.correctAnswer && !question.answers.includes(a))
          : undefined) ??
        (Array.isArray(data.answers)
          ? data.answers.find((_: string, i: number) => i !== data.correctAnswer)
          : undefined) ??
        "";
      // Pull the placeholder-filled question off the latest state, sub in
      // the real candidate text, and remember the result so we can hand it
      // to the auto-score call after setQuestions commits.
      let scoredQuestion: Question | undefined;
      setQuestions((prev) =>
        prev.map((q) => {
          if (q.id !== questionId) return q;
          const updatedAnswers = [...q.answers];
          updatedAnswers[newIndex] = candidate;
          const next = sortQuestionAnswers(
            normalizeAnswerCapitalization({
              ...q,
              answers: updatedAnswers,
              duplicate_warning: data.duplicate_warning,
            }),
          );
          scoredQuestion = next;
          return next;
        }),
      );
      toast({
        variant: "info",
        title: "Option added",
        description: `Option ${String.fromCharCode(65 + newIndex)} has been generated.`,
      });
      if (data.duplicate_warning?.flagged) {
        toast({ title: "Possible duplicate", description: "This item may be similar to an existing item." });
      }
      // Auto-re-score against the new option set.
      if (scoredQuestion) void scoreAddedItem(questionId, scoredQuestion);
    } catch (error) {
      console.error("Failed to add option:", error);
      // Revert the placeholder slot on failure.
      setQuestions((prev) =>
        prev.map((q) => (q.id === questionId ? { ...q, answers: q.answers.filter((_, i) => i !== newIndex) } : q)),
      );
      toast({ title: "Couldn't add option", description: "Please try again." });
    } finally {
      markAnswerAdding(questionId, newIndex, false);
    }
  };

  // On mount, replay any regenerations that were in flight when the page
  // was refreshed. The original fetch died with the navigation, so we
  // re-fire the handler — same TTL-guarded list flows back through the
  // normal completion path (which clears the persisted entry). Gated by
  // a ref so it only attempts once per mount, even though the effect
  // depends on questions.length.
  const didReplayRegenRef = useRef(false);
  useEffect(() => {
    if (didReplayRegenRef.current) return;
    if (questions.length === 0 || !blueprint) return;
    didReplayRegenRef.current = true;
    const pending = loadPendingRegens();
    for (const entry of pending.questions) {
      if (questions.some((q) => q.id === entry.questionId)) {
        void handleRegenerateQuestion(entry.questionId, entry.targetLevel, entry.blueprintDecision);
      } else {
        // Question no longer exists in this test — drop the stale entry.
        unmarkQuestionPending(entry.questionId);
      }
    }
    for (const entry of pending.answers) {
      const q = questions.find((x) => x.id === entry.questionId);
      if (q && entry.answerIndex >= 0 && entry.answerIndex < q.answers.length) {
        void handleRegenerateAnswer(entry.questionId, entry.answerIndex);
      } else {
        unmarkAnswerPending(entry.questionId, entry.answerIndex);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questions.length, blueprint]);

  const handleUndoRegenerate = () => {
    if (regenerationHistory.length > 0) {
      const lastAction = regenerationHistory[regenerationHistory.length - 1];
      
      if (lastAction.action === 'bulk-question') {
        // Handle bulk regeneration undo - restore all previous versions
        const previousQuestions = lastAction.previousVersion as Question[];
        setQuestions(
          questions.map((q) => {
            const previousVersion = previousQuestions.find(pq => pq.id === q.id);
            return previousVersion || q;
          })
        );
        // Reverted options — clear stale scores for every restored question.
        clearSocratesFor(...previousQuestions.map(q => q.id));

        toast({
          title: "Bulk Regeneration Undone",
          description: `Reverted regeneration of ${previousQuestions.length} questions`,
        });
      } else if (lastAction.action === 'edit') {
        setQuestions(
          questions.map((q) =>
            q.id === lastAction.questionId
              ? lastAction.previousVersion as Question
              : q
          )
        );
        // Options reverted to a prior state — clear the stale Socrates result.
        clearSocratesFor(lastAction.questionId);
        toast({
          title: "Edit Undone",
          description: "Reverted your last manual edit",
        });
      } else if (lastAction.action === 'delete') {
        // Re-insert the deleted item at its original position.
        const restored = lastAction.previousVersion as Question;
        const at = Math.min(lastAction.index ?? questions.length, questions.length);
        const next = [...questions];
        next.splice(at, 0, restored);
        setQuestions(next);
        toast({
          title: "Deletion Undone",
          description: "Restored the deleted item.",
        });
      } else {
        // Handle single question/answer undo — restore against the latest
        // questions array so concurrent operations elsewhere survive.
        setQuestions((prev) =>
          prev.map((q) =>
            q.id === lastAction.questionId
              ? lastAction.previousVersion as Question
              : q
          )
        );
        // Options reverted — clear the stale Socrates result.
        clearSocratesFor(lastAction.questionId);

        // Atomic undo for a confirmed blueprint amendment: reverse exactly
        // this operation's move (not a snapshot), so a concurrent amendment's
        // valid change is preserved.
        if (lastAction.blueprintInverse) {
          inverseBlueprintMove(setBlueprint, lastAction.blueprintInverse);
        }

        const actionType = lastAction.action === 'question'
          ? 'question'
          : `answer option ${String.fromCharCode(65 + (lastAction.answerIndex || 0))}`;

        toast({
          title: "Regeneration Undone",
          description: `Reverted ${actionType} regeneration`,
        });
      }
      
      // Remove the last action from history
      setRegenerationHistory(prev => prev.slice(0, -1));
    }
  };

  // Reset is irreversible (drops all per-question regen history), so each
  // of the three "Reset to Original" buttons (desktop sidebar + two mobile
  // panels) opens a single shared confirmation dialog instead of firing
  // directly. The handler below runs only after the user confirms.
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);

  const handleResetToOriginal = () => {
    setResetConfirmOpen(false);
    if (regenerationHistory.length > 0) {
      // Get the original version (first item in history)
      const originalVersion = regenerationHistory[0].previousVersion;
      const questionId = regenerationHistory[0].questionId;

      // Only handle single question resets (not bulk)
      if (!Array.isArray(originalVersion)) {
        setQuestions(
          questions.map((q) =>
            q.id === questionId ? originalVersion : q
          )
        );
        // Options reset to original — the Socrates result was against a
        // different version; clear it.
        clearSocratesFor(questionId);

        // Clear all history for this question
        setRegenerationHistory(prev =>
          prev.filter(item => item.questionId !== questionId)
        );

        toast({
          title: "Reset to Original",
          description: "Question restored to original generated version",
        });
      }
    }
  };

  const handleRegenerateTopicUnlocked = async (topicId: string) => {
    const topicUnlockedQuestions = questions.filter(q => q.topicId === topicId && !q.locked);

    if (topicUnlockedQuestions.length === 0) {
      toast({
        title: "No Unlocked Items",
        description: "All questions in this topic are locked",
      });
      return;
    }

    const bulkHistoryEntry = {
      questionId: `bulk-regenerate-${topicId}`,
      previousVersion: topicUnlockedQuestions.map(q => ({ ...q })),
      action: 'bulk-question' as const
    };

    setRegenerationHistory(prev => [...prev, bulkHistoryEntry]);

    const topic = blueprint?.topics.find(t => t.id === topicId);
    const topicName = topic?.name || 'Unknown Topic';

    // Mark every affected question as regenerating up front so all their
    // cards grey out together, not one-by-one as promises resolve.
    topicUnlockedQuestions.forEach((q) => markQuestionRegenerating(q.id, true));
    // Regenerate all unlocked questions in parallel
    try {
      const regenerationPromises = topicUnlockedQuestions.map(async (question) => {
        const response = await apiClient("/api/questions/regenerate", {
          method: "POST",
          body: JSON.stringify({
            question: {
              stem: question.stem,
              answers: question.answers,
              correctAnswer: question.correctAnswer,
              level: question.level,
              difficulty: question.difficulty,
            },
            topicName,
            grade: readingLevelToGrade(config?.readingLevel),
            standards: question.standards,
          }),
        });

        if (!response.ok) throw await httpErrorFrom(response);
        const data = await response.json();
        return { id: question.id, ...data };
      });

      const results = await Promise.all(regenerationPromises);

      // Clear stale Socrates results for every question whose options just
      // changed. Do this before setQuestions so the UI never shows a stale
      // score even for one render cycle.
      clearSocratesFor(...topicUnlockedQuestions.map(q => q.id));
      setQuestions(
        questions.map((q) => {
          if (q.topicId !== topicId || q.locked) return q;
          const regenerated = results.find(r => r.id === q.id);
          if (regenerated) {
            return sortQuestionAnswers(normalizeAnswerCapitalization({ ...q, stem: regenerated.stem, answers: regenerated.answers, correctAnswer: regenerated.correctAnswer }));
          }
          return q;
        })
      );

      toast({
        variant: "info",
        title: "Topic Items Regenerated",
        description: `Regenerated ${topicUnlockedQuestions.length} unlocked questions in ${topicName}`,
      });
    } catch (error) {
      console.error("Failed to regenerate topic questions:", error);
      toast({
        ...regenerationToast(error, "Some questions could not be regenerated. Please try again."),
      });
      setRegenerationHistory(prev => prev.slice(0, -1));
    } finally {
      topicUnlockedQuestions.forEach((q) => markQuestionRegenerating(q.id, false));
    }
  };

  const handleToggleLockAll = (checked: boolean) => {
    setQuestions(questions.map(q => ({ ...q, locked: checked })));
  };

  const handleChatWithItem = (question: Question) => {
    const questionNumber = questions.findIndex(q => q.id === question.id) + 1;
    setFocusedQuestion({
      number: questionNumber,
      stem: question.stem,
      answers: question.answers,
      correctAnswer: question.correctAnswer,
      level: question.level,
    });
    setIsMobilePanelCollapsed(false); // Expand chat on mobile if collapsed
  };

  const handleClearFocus = () => {
    setFocusedQuestion(null);
  };

  const handleSocratesUpdate = (questionId: string, score: SocratesQuestionData) => {
    setSocratesMap(prev => {
      const next = new Map(prev);
      next.set(questionId, score);
      return next;
    });
    // Mirror onto the question so the score survives a refresh.
    setQuestions(prev => prev.map(q =>
      q.id === questionId ? { ...q, socratesScore: score, socratesScoreVoided: false } : q,
    ));
  };

  // Card Rescore path. The isolated /api/questions/score endpoint scores the
  // displayed answers verbatim, so its letters are display-aligned and the
  // pre-sort→display map must never accompany them. Merge score + cleared map
  // onto the CURRENT question under a functional updater (the scoreAddedItem
  // pattern) so a newer edit or regeneration that landed while the rescore
  // request was in flight is never reverted by the card's pre-request
  // snapshot. The merge applies ONLY if the current question still matches
  // the content that was POSTed: a stale response for content that was since
  // superseded must not overwrite the newer score with one computed against
  // the old answers.
  const handleSocratesRescore = (
    questionId: string,
    score: SocratesQuestionData,
    submitted: { stem?: string; answers?: string[]; correctAnswer?: number },
  ) => {
    const current = questionsRef.current.find(q => q.id === questionId);
    const matches =
      !!current &&
      current.stem === submitted.stem &&
      current.correctAnswer === submitted.correctAnswer &&
      (submitted.answers?.length ?? -1) === current.answers.length &&
      (submitted.answers ?? []).every((a, i) => a === current.answers[i]);
    // Content changed while the request was in flight — the response was
    // computed against content that is no longer the current question, so
    // discard it from both the render map and the persisted score: never
    // overwrite a score for the newer content with one for the old answers.
    if (!matches) return;
    setSocratesMap(prev => {
      const next = new Map(prev);
      next.set(questionId, score);
      return next;
    });
    setQuestions(prev => prev.map(q =>
      q.id === questionId
        ? { ...q, socratesScore: score, socratesScoreVoided: false, answerLetterMap: undefined }
        : q,
    ));
  };

  // Clears stale Socrates results for one or more question IDs so the panel
  // never renders a score against a different set of options. Call this
  // whenever options are mutated (regen, undo, reset). The score badge will
  // be absent until the user explicitly re-scores.
  const clearSocratesFor = (...ids: string[]) => {
    const idSet = new Set(ids);
    setSocratesMap(prev => {
      const next = new Map(prev);
      for (const id of ids) next.delete(id);
      return next;
    });
    setSocratesLoadingIds(prev => {
      const next = new Set(prev);
      for (const id of ids) next.delete(id);
      return next;
    });
    // Also clear the persisted copy on the question (and mark it voided)
    // so a refresh doesn't pull a stale score back from the Blacksmith
    // session for this slot.
    setQuestions(prev => prev.map(q =>
      idSet.has(q.id)
        ? { ...q, socratesScore: undefined, socratesScoreVoided: true }
        : q,
    ));
  };

  const handleAcceptTest = () => {
    setLocation("/create/finalize");
  };

  const handleBack = () => {
    setLocation("/create/review-blueprint");
  };

  const totalKnowledge = questions.filter((q) => q.level === "Knowledge").length;
  const totalUnderstanding = questions.filter((q) => q.level === "Understanding").length;
  const totalHigherOrder = questions.filter((q) => q.level === "Higher-Order Thinking").length;

  // Calculate toast position based on layout state
  const getToastPosition = () => {
    if (layoutState.isCondensed) {
      // On mobile/condensed layout
      if (isMobilePanelCollapsed) {
        return '20rem'; // Far above collapsed panel
      } else {
        return '36rem'; // Far above expanded panel
      }
    } else {
      // On desktop/wide layout
      return '1rem'; // Bottom right corner
    }
  };

  // Options/Summary pieces, composed differently per slot.
  const summaryBlock = (
    <div>
      <h3 className="font-semibold mb-3">Summary</h3>
      <div className="space-y-2 text-sm">
        <div className="flex justify-between">
          <span className="text-muted-foreground">Total questions</span>
          <span className="font-semibold tabular-nums">{questions.length}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-muted-foreground">Knowledge</span>
          <span className="font-semibold" data-testid="summary-count-knowledge">{totalKnowledge}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-muted-foreground">Understanding</span>
          <span className="font-semibold" data-testid="summary-count-understanding">{totalUnderstanding}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-muted-foreground">Application+</span>
          <span className="font-semibold" data-testid="summary-count-application-plus">{totalHigherOrder}</span>
        </div>
      </div>
    </div>
  );
  const addItemBlock = (
    <AddItemControl
      topics={blueprint?.topics ?? []}
      total={questions.length}
      cap={MAX_TEST_ITEMS}
      busy={addingItem}
      generatingTopicId={addingTopicId}
      run={addItemRun}
      onAdd={handleAddItem}
    />
  );
  const undoFinalizeBlock = (
    <div className="space-y-2">
      <Button
        variant="secondary"
        className="w-full"
        onClick={handleUndoRegenerate}
        disabled={regenerationHistory.length === 0}
        data-testid="button-undo-regenerate"
      >
        <Undo2 className="w-4 h-4 mr-2" />
        Undo Last
      </Button>
      {regenerationHistory.length > 1 && (
        <Button
          variant="outline"
          size="sm"
          className="w-full"
          onClick={() => setResetConfirmOpen(true)}
          data-testid="button-reset-original"
        >
          Reset to Original
        </Button>
      )}
      <Button className="w-full" onClick={handleAcceptTest} data-testid="button-accept">
        Finalize Items
        <ArrowRight className="w-4 h-4 ml-2" />
      </Button>
    </div>
  );

  return (
    <div 
      className="min-h-screen bg-background"
      style={{
        '--toast-bottom': getToastPosition()
      } as React.CSSProperties}
    >
      <Header pageTitle="Create Test" />

      <main className="container mx-auto px-6 py-12 max-w-7xl">
        <ProgressIndicator currentStep={5} />

        {/* minmax(0,1fr) instead of 1fr lets the left column shrink past
            its intrinsic content width — without this, a long topic-tab
            label with whitespace-nowrap blows past the column and pushes
            the whole page wider than the viewport. */}
        <div className={`lg:grid ${assistantNarrow ? "lg:grid-cols-[minmax(0,1fr)_288px] xl:grid-cols-[240px_minmax(0,1fr)_288px]" : "lg:grid-cols-[minmax(0,1fr)_384px] xl:grid-cols-[240px_minmax(0,1fr)_384px]"} lg:gap-6`}>
          {/* Left rail (≥xl): topic navigator pinned at the top, options/summary
              aligned to the bottom of the viewport. Narrower screens use the
              compact horizontal selector + the right/bottom panels. */}
          <div ref={railRef} className="hidden xl:flex xl:flex-col sticky top-6 h-[calc(100vh-3rem)]">
            <TopicNavigator
              className="flex-1 min-h-0"
              topics={blueprint.topics}
              activeId={activeId}
              onSelect={scrollToTopic}
              counts={Object.fromEntries(questionsByTopic.map(({ topic, questions: qs }) => [topic.id, qs.length]))}
            />
            {/* Options pinned to the bottom of the rail, in the same condensable
                card as the medium view: Summary + add item are hidden by default
                and only appear when the toggle is clicked, so they never push
                Undo/Finalize off the bottom on shorter screens. The navigator
                above flexes and scrolls so a long topic list can't push this
                card (and its Finalize button) out of frame. */}
            <Card className="mt-4 shrink-0 flex flex-col min-h-0 overflow-hidden">
              <CardContent className="pt-4 pb-4 flex flex-col gap-3 min-h-0 overflow-hidden">
                <button
                  type="button"
                  onClick={() => {
                    userSetRail.current = true;
                    setRailCondensed((c) => !c);
                  }}
                  className="flex w-full items-center justify-between text-xs font-semibold uppercase tracking-wide text-muted-foreground hover:text-foreground"
                  data-testid="button-condense-options-rail"
                >
                  <span>Summary &amp; add item</span>
                  {railCondensed ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
                </button>
                {/* Keep a generating indicator visible when the card is
                    collapsed so the user knows an item is still cooking. */}
                {railCondensed && addingItem && (
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Loader2 className="w-3 h-3 animate-spin" />
                    Generating an item…
                  </div>
                )}
                {!railCondensed && (
                  <div className="space-y-5 min-h-0 overflow-y-auto">
                    {summaryBlock}
                    <div className="pt-4 border-t border-border">{addItemBlock}</div>
                  </div>
                )}
                <div className="shrink-0 pt-3 border-t border-border">
                  {undoFinalizeBlock}
                </div>
              </CardContent>
            </Card>
          </div>

          <div className={`min-w-0 lg:pb-0 ${isMobilePanelCollapsed ? 'pb-24' : 'pb-80'}`}>
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-foreground mb-2">
                Review and Revise Your Items
              </h1>
            </div>

            {/* Compact topic nav for < xl — sticks to the top while scrolling. */}
            <div className="xl:hidden sticky top-0 z-20 bg-background">
              <TopicSelector
                topics={blueprint.topics}
                value={activeId}
                onChange={scrollToTopic}
              />
            </div>

            {/* All items flow in one vertical list, segmented by big topic headers. */}
            <div className="space-y-12 mt-6">
              {questionsByTopic.map(({ topic, questions: topicQuestions }) => (
                <section
                  key={topic.id}
                  id={`topic-${topic.id}`}
                  className="scroll-mt-24 space-y-6"
                  data-testid={`topic-section-${topic.id}`}
                >
                  <div className="flex justify-between items-end gap-3 border-b border-border pb-2">
                    <h2 className="text-2xl font-bold text-foreground flex-1 min-w-0 wrap-break-word">{topic.name}</h2>
                    {SHOW_TOPIC_REGEN && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="shrink-0"
                        onClick={() => handleRegenerateTopicUnlocked(topic.id)}
                        disabled={topicQuestions.every(q => q.locked)}
                        data-testid={`button-regenerate-topic-${topic.id}`}
                      >
                        Regenerate unlocked items for this topic <RefreshCw className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                  {topicQuestions.map((question) => (
                    <QuestionCard
                      key={question.id}
                      question={question}
                      // Global position across the whole test.
                      number={questions.indexOf(question) + 1}
                      onUpdate={handleUpdateQuestion}
                      onRegenerate={handleRegenerateQuestion}
                      onRegenerateAnswer={handleRegenerateAnswer}
                      onDelete={handleDeleteQuestion}
                      onAddOption={handleAddOption}
                      topicName={topic.name}
                      readingLevel={config?.readingLevel}
                      onChat={handleChatWithItem}
                      socratesData={socratesMap.get(question.id)}
                      socratesLoading={socratesLoadingIds.has(question.id)}
                      onSocratesUpdate={handleSocratesUpdate}
                      onSocratesRescore={handleSocratesRescore}
                      questionIndexToTaskId={socratesIndexToTaskId}
                      allQuestions={questions}
                      isRegeneratingQuestion={regeneratingQuestionIds.has(question.id)}
                      regeneratingAnswerIndices={Array.from(regeneratingAnswers.get(question.id) ?? [])}
                      addingAnswerIndices={Array.from(addingAnswers.get(question.id) ?? [])}
                      highlight={highlightFor(question.id)}
                      regeneratedOptions={Array.from(regeneratedOptionIds.get(question.id) ?? [])}
                      onSeen={handleItemSeen}
                      onClearOption={handleOptionSeen}
                    />
                  ))}
                </section>
              ))}
            </div>

            <div className="flex justify-start mt-8">
              <Button variant="outline" onClick={handleBack} data-testid="button-back">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Blueprint
              </Button>
            </div>
          </div>

          {/* Desktop sidebar — sticky column whose total height toggles
              between full viewport and 70vh via the chat's height chevron
              (toggleMode="half", state shared with the blueprint pages).
              Options Card + assistant share the column; flex layout keeps
              the assistant taking the remaining space regardless of the
              column height. */}
          <div className={`hidden lg:flex lg:flex-col sticky top-6 ${assistantHalfHeight ? "h-[70vh]" : "h-[calc(100vh-3rem)]"} gap-4`}>
            {/* Options card (lg only; moves to the left rail at xl). The summary
                + add-item part is condensable to give the assistant more room;
                Undo/Finalize stay pinned and visible. Capped to half the column
                so the assistant always keeps room. */}
            <Card className="xl:hidden flex flex-col min-h-0 max-h-[55%]">
              <CardContent className="pt-4 pb-4 flex flex-col gap-3 min-h-0 overflow-hidden">
                <button
                  type="button"
                  onClick={() => setCondenseOptions((c) => !c)}
                  className="flex w-full items-center justify-between text-xs font-semibold uppercase tracking-wide text-muted-foreground hover:text-foreground"
                  data-testid="button-condense-options"
                >
                  <span>Summary &amp; add item</span>
                  {condenseOptions ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
                </button>
                {!condenseOptions && (
                  <div className="grid grid-cols-2 gap-4 min-h-0 overflow-y-auto">
                    {summaryBlock}
                    {addItemBlock}
                  </div>
                )}
                <div className="shrink-0 pt-3 border-t border-border">
                  {undoFinalizeBlock}
                </div>
              </CardContent>
            </Card>

            {/* Assistant fills the remaining height; scrolls internally.
                Chevron in the header toggles the column between full /
                70vh via useChatHalfHeight() (shared with blueprint pages). */}
            <div className="flex-1 min-h-0">
              <SmartAIChat
                className="h-full"
                context="review-test"
                focusedQuestion={focusedQuestion}
                onClearFocus={handleClearFocus}
                toggleMode="half"
              />
            </div>
          </div>

          {/* Mobile/Tablet Bottom Fixed Panel */}
          <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-background border-t shadow-lg z-50">
            {isMobilePanelCollapsed ? (
              /* Collapsed State - Header bar on left, Options on right */
              <div className="flex min-h-16 h-auto max-h-24">
                {/* Collapsed AI Assistant Header */}
                <div 
                  className="flex-none flex items-center"
                  onClick={() => setIsMobilePanelCollapsed(false)}
                >
                  <div className="flex items-center justify-between p-4 bg-secondary/10 cursor-pointer hover-elevate min-h-16 border-r w-full">
                    <div className="flex items-center gap-2">
                      <Sparkles className="w-5 h-5 text-secondary" />
                      <h3 className="font-semibold text-foreground">AI Assistant</h3>
                    </div>
                    <Button size="icon" variant="ghost" className="h-8 w-8">
                      <ChevronUp className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                
                {/* Options Panel - Takes remaining space when AI is collapsed */}
                <div className="flex-1 flex items-center p-2 overflow-y-auto">
                  <div className="flex flex-wrap items-center gap-1">
                    <h3 className="font-semibold text-foreground mr-2">Options</h3>
                    <AddItemButton
                      topics={blueprint.topics}
                      total={questions.length}
                      cap={MAX_TEST_ITEMS}
                      busy={addingItem}
                      generatingTopicId={addingTopicId}
                      run={addItemRun}
                      onAdd={handleAddItem}
                      className="text-xs px-2 py-1 h-auto"
                    />
                    {SHOW_LOCK_UI && (
                      <div className="flex items-center gap-1">
                        <label className="text-xs text-muted-foreground">Lock all</label>
                        <Switch
                          checked={lockAll}
                          onCheckedChange={handleToggleLockAll}
                          className="scale-75"
                          data-testid="switch-lock-all-mobile"
                        />
                      </div>
                    )}
                    <Button
                      variant="secondary"
                      size="sm"
                      className="text-xs px-2 py-1 h-auto"
                      onClick={handleUndoRegenerate}
                      disabled={regenerationHistory.length === 0}
                      data-testid="button-undo-mobile"
                    >
                      <Undo2 className="w-3 h-3 mr-1" />
                      Undo
                    </Button>
                    {regenerationHistory.length > 1 && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-xs px-2 py-1 h-auto"
                        onClick={() => setResetConfirmOpen(true)}
                        data-testid="button-reset-mobile"
                      >
                        Reset
                      </Button>
                    )}
                    <Button
                      size="sm"
                      className="text-xs px-2 py-1 h-auto"
                      onClick={handleAcceptTest}
                      data-testid="button-finalize-mobile"
                    >
                      Finalize <ArrowRight className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              </div>
            ) : (
              /* Expanded State - Full panel */
              <div className="flex h-80">
                {/* AI Assistant Panel - 75% */}
                <div className="w-3/4 border-r">
                  <div className="h-full">
                    <SmartAIChat
                      context="review-test"
                      className="h-full"
                      isCollapsible={true}
                      defaultCollapsed={false}
                      onCollapseChange={setIsMobilePanelCollapsed}
                      focusedQuestion={focusedQuestion}
                      onClearFocus={handleClearFocus}
                      showWidthToggle={false}
                    />
                  </div>
                </div>
                
                {/* Options Panel - 25% */}
                <div className="w-1/4 p-4 overflow-y-auto">
                  <h3 className="font-semibold mb-3 text-sm">Options</h3>
                  <div className="space-y-3">
                    {SHOW_LOCK_UI && (
                      <div className="flex items-center justify-between">
                        <Label htmlFor="lock-all-mobile" className="text-xs">Lock all items in test</Label>
                        <Switch
                          id="lock-all-mobile"
                          checked={lockAll}
                          onCheckedChange={handleToggleLockAll}
                          className="scale-75"
                          data-testid="switch-lock-all-mobile"
                        />
                      </div>
                    )}
                    <Button
                      variant="secondary"
                      size="sm"
                      className="w-full text-xs"
                      onClick={handleUndoRegenerate}
                      disabled={regenerationHistory.length === 0}
                      data-testid="button-undo-mobile"
                    >
                      <Undo2 className="w-3 h-3 mr-1" />
                      Undo
                    </Button>
                    {regenerationHistory.length > 1 && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full text-xs"
                        onClick={() => setResetConfirmOpen(true)}
                        data-testid="button-reset-mobile"
                      >
                        Reset to Original
                      </Button>
                    )}
                    <Button
                      size="sm"
                      className="w-full text-xs"
                      onClick={handleAcceptTest}
                      data-testid="button-finalize-mobile"
                    >
                      Finalize Items
                      <ArrowRight className="w-3 h-3 ml-1" />
                    </Button>
                  </div>

                  <div className="mt-3 pt-3 border-t">
                    <h4 className="font-semibold mb-2 text-xs">Summary</h4>
                    <div className="space-y-1 text-xs">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Questions</span>
                        <span className="font-semibold">{questions.length}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Knowledge</span>
                        <span className="font-semibold">{totalKnowledge}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Understanding</span>
                        <span className="font-semibold">{totalUnderstanding}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Application+</span>
                        <span className="font-semibold">{totalHigherOrder}</span>
                      </div>
                      {SHOW_LOCK_UI && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Locked</span>
                          <span className="font-semibold">
                            {questions.filter((q) => q.locked).length}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="mt-3 pt-3 border-t">
                    <AddItemButton
                      topics={blueprint.topics}
                      total={questions.length}
                      cap={MAX_TEST_ITEMS}
                      busy={addingItem}
                      generatingTopicId={addingTopicId}
                      run={addItemRun}
                      onAdd={handleAddItem}
                      className="w-full"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      <AlertDialog open={resetConfirmOpen} onOpenChange={setResetConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reset all items to the original generation?</AlertDialogTitle>
            <AlertDialogDescription>
              This rolls every regeneration and manual edit back to what was
              first generated. The regeneration history will be cleared and
              this can't be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleResetToOriginal}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="button-confirm-reset-original"
            >
              Reset to Original
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
